import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import Placeholder from 'components/consumer/Placeholder';
import RatioContainer from 'components/consumer/RatioContainer';
import classNames from 'classnames';
import PDPNameSectionPlaceholder from './PDPNameSection/PDPNameSectionPlaceholder';
import PDPShopTabsBarPlaceholder from './PDPShopTabsBar/PDPShopTabsBarPlaceholder';
import PDPChooseYourOptionsPlaceholder from './PDPChooseYourOptions/PDPChooseYourOptionsPlaceholder';
import PDPProtectionPlanSelectorPlaceholder from './PDPProtectionPlanSelector/PDPProtectionPlanSelectorPlaceholder';
import PDPAddToBagSectionPlaceholder from './PDPAddToBagSection/PDPAddToBagSectionPlaceholder';
import buildPdpDesktopHeroLcpUrl from './PDPDesktopHeroGallery/helpers/buildPdpDesktopHeroLcpUrl';
import usePDPDeepLinkVariantMismatch from './hooks/usePDPDeepLinkVariantMismatch';
import { getPaintedHero } from './PDPDesktopHeroGallery/helpers/lastPaintedHero';
import useIsLayoutRevampVariant from './useIsLayoutRevampVariant';

/**
 * Context-region Suspense fallback: section placeholders for the rail, and the
 * resolved server hero image (when available) so LCP can paint without waiting
 * on basicDetails / stock / protection / options.
 */
const PDPContextRegionPlaceholder = t0 => {
  const $ = _c(42);
  const {
    productHeroImage: t1,
    productCylindoHeroImage: t2,
    className: parentClassName,
    isEditOrder: t3,
    pdpIsModularBYOProduct: t4
  } = t0;
  const productHeroImage = t1 === undefined ? null : t1;
  const productCylindoHeroImage = t2 === undefined ? null : t2;
  const isEditOrder = t3 === undefined ? false : t3;
  const pdpIsModularBYOProduct = t4 === undefined ? false : t4;
  const isLayoutRevamp = useIsLayoutRevampVariant() && !pdpIsModularBYOProduct && !isEditOrder;
  const t5 = productCylindoHeroImage?.defaultUpholsteryValue;
  const t6 = productCylindoHeroImage?.defaultOrientation;
  let t7;
  if ($[0] !== t5 || $[1] !== t6) {
    t7 = {
      defaultUpholsteryValue: t5,
      defaultOrientation: t6
    };
    $[0] = t5;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const isDeepLinkMismatch = usePDPDeepLinkVariantMismatch(t7);
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getPaintedHero();
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const alreadyPainted = t8;
  const desktopHeroSrc = alreadyPainted || (isDeepLinkMismatch ? null : productHeroImage?.src || productCylindoHeroImage?.src || null);
  let t9;
  if ($[4] !== desktopHeroSrc) {
    t9 = desktopHeroSrc ? buildPdpDesktopHeroLcpUrl(desktopHeroSrc) ?? desktopHeroSrc : null;
    $[4] = desktopHeroSrc;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const desktopHeroHref = t9;
  const desktopHeroAlt = productHeroImage?.alt || productCylindoHeroImage?.alt || "";
  const mobileHeroSrc = alreadyPainted || (isDeepLinkMismatch ? null : productHeroImage?.src || productCylindoHeroImage?.src || null);
  const mobileHeroAlt = productHeroImage?.alt || productCylindoHeroImage?.alt || "";
  let t10;
  if ($[6] !== parentClassName) {
    t10 = classNames("hidden lg:flex mx-auto my-0 py-0 flex-row-reverse !pt-2 !pb-8 px-5 md:px-[3.333vw] min-[1500px]:px-[50px] max-w-[1800px]", parentClassName);
    $[6] = parentClassName;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <PDPNameSectionPlaceholder />;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== isLayoutRevamp) {
    t12 = <PDPShopTabsBarPlaceholder isLayoutRevamp={isLayoutRevamp} />;
    $[9] = isLayoutRevamp;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  let t14;
  let t15;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PDPChooseYourOptionsPlaceholder />;
    t14 = <PDPProtectionPlanSelectorPlaceholder />;
    t15 = <PDPAddToBagSectionPlaceholder />;
    $[11] = t13;
    $[12] = t14;
    $[13] = t15;
  } else {
    t13 = $[11];
    t14 = $[12];
    t15 = $[13];
  }
  let t16;
  if ($[14] !== t12) {
    t16 = <div className="flex-none min-w-[460px] w-[460px] [@media(width:1024px)]:min-w-[400px] [@media(width:1024px)]:w-[400px]">{t11}{t12}{t13}{t14}{t15}</div>;
    $[14] = t12;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="min-h-[66px]" />;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      maxWidth: "calc((100vh - 194px) / 0.6666666666666666)"
    };
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] !== desktopHeroAlt || $[19] !== desktopHeroHref || $[20] !== productHeroImage?.height || $[21] !== productHeroImage?.width) {
    t19 = <div className="flex-1 mr-12">{t17}<div className="sticky top-8"><div className="mb-8 ml-auto mr-auto relative" style={t18}><RatioContainer ratio={0.6666666666666666}>{desktopHeroHref ? <img className="h-full w-full object-contain object-center select-none" src={desktopHeroHref} alt={desktopHeroAlt} width={productHeroImage?.width || 1600} height={productHeroImage?.height || 2400} loading="eager" fetchPriority="high" decoding="async" /> : <Placeholder loading={true} type="rect" />}</RatioContainer></div></div></div>;
    $[18] = desktopHeroAlt;
    $[19] = desktopHeroHref;
    $[20] = productHeroImage?.height;
    $[21] = productHeroImage?.width;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== t10 || $[24] !== t16 || $[25] !== t19) {
    t20 = <div className={t10} data-xc="PDPContextRegionPlaceholder-desktop">{t16}{t19}</div>;
    $[23] = t10;
    $[24] = t16;
    $[25] = t19;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== parentClassName) {
    t21 = classNames("flex flex-col lg:hidden", parentClassName);
    $[27] = parentClassName;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="pl-5 pr-5 md:pl-[3.333vw] md:pr-[3.333vw]"><PDPNameSectionPlaceholder /></div>;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== mobileHeroAlt || $[31] !== mobileHeroSrc || $[32] !== productHeroImage?.height || $[33] !== productHeroImage?.width) {
    t23 = mobileHeroSrc ? <img className="mb-1.5 w-full object-contain object-center" src={mobileHeroSrc} alt={mobileHeroAlt} width={productHeroImage?.width || 800} height={productHeroImage?.height || 800} loading="eager" fetchPriority="high" decoding="async" style={{
      width: "100vw",
      aspectRatio: "1/1",
      objectFit: "contain"
    }} /> : <Placeholder className="mb-1.5" loading={true} type="rect" style={{
      width: "100vw",
      aspectRatio: "1/1"
    }} />;
    $[30] = mobileHeroAlt;
    $[31] = mobileHeroSrc;
    $[32] = productHeroImage?.height;
    $[33] = productHeroImage?.width;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="pl-5 pr-5 md:pl-[3.333vw] md:pr-[3.333vw]"><PDPChooseYourOptionsPlaceholder /><PDPProtectionPlanSelectorPlaceholder /><PDPAddToBagSectionPlaceholder /></div>;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t21 || $[37] !== t23) {
    t25 = <div className={t21} data-xc="PDPContextRegionPlaceholder-mobile">{t22}{t23}{t24}</div>;
    $[36] = t21;
    $[37] = t23;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] !== t20 || $[40] !== t25) {
    t26 = <>{t20}{t25}</>;
    $[39] = t20;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  return t26;
};
PDPContextRegionPlaceholder.propTypes = {
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  className: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool
};
export default PDPContextRegionPlaceholder;